import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import createNewTicketApi from '../../../middleware/api/Support/RaiseTicket/createNewTicketApi'
import { AppDispatch, RootState } from '../../../stateStore'
import { ValidationError } from '../../../types/commonTypes'
import {
  CreateNewTicket,
  GetDepartmentListForRaise,
  createNewTicketSliceState,
} from '../../../types/Support/RaiseNewTicket/createNewTicketTypes'

const createNewTicket = createAsyncThunk<
  { ticketId: number },
  CreateNewTicket,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'support/createNewTicket',
  async (raiseNewTicket: CreateNewTicket, thunkApi) => {
    try {
      return await createNewTicketApi.createNewTicket(raiseNewTicket)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const uploadSupportTicketsDocuments = createAsyncThunk(
  'support/uploadSupportTicketsDocuments',
  async (prepareObject: { ticketId: number; file: FormData }, thunkApi) => {
    try {
      return await createNewTicketApi.uploadSupportTicketsDocuments(
        prepareObject,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getDepartmentListForRaise = createAsyncThunk(
  'support/getDepartmentListForRaise',
  async (_, thunkApi) => {
    try {
      return await createNewTicketApi.getDepartmentListForRaise()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const initialCreateTicketState: createNewTicketSliceState = {
  createNewTicket: {} as CreateNewTicket,
  isLoading: ApiLoadingState.idle,
  getDepartmentListForRaise: [],

  fromDate: '',
  toDate: '',
}

const createNewTicketSlice = createSlice({
  name: 'support',
  initialState: initialCreateTicketState,
  reducers: {
    clearNewTicketFields: (state) => {
      state.createNewTicket = {} as CreateNewTicket
    },
    setFromDate: (state, action) => {
      state.fromDate = action.payload
    },
    setToDate: (state, action) => {
      state.toDate = action.payload
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getDepartmentListForRaise.fulfilled, (state, action) => {
      state.isLoading = ApiLoadingState.succeeded
      state.getDepartmentListForRaise = action.payload
    })
    builder.addCase(getDepartmentListForRaise.pending, (state) => {
      state.isLoading = ApiLoadingState.loading
    })
    builder.addCase(uploadSupportTicketsDocuments.fulfilled, (state) => {
      state.isLoading = ApiLoadingState.succeeded
    })
  },
})

const createNewTicketThunk = {
  createNewTicket,
  uploadSupportTicketsDocuments,
  getDepartmentListForRaise,
}

const getDepartmentList = (state: RootState): GetDepartmentListForRaise[] =>
  state.raiseNewTicket.getDepartmentListForRaise

const getFromDateValue = (state: RootState): string =>
  state.sqaAuditReport.fromDate

const getToDateValue = (state: RootState): string => state.sqaAuditReport.toDate

const createNewTicketSelectors = {
  getDepartmentList,
  getFromDateValue,
  getToDateValue,
}

export const createNewTicketService = {
  ...createNewTicketThunk,
  actions: createNewTicketSlice.actions,
  selectors: createNewTicketSelectors,
}

export default createNewTicketSlice.reducer
